import { Module, PATH_PROJECT_SETTLEMENTS_DETAILS } from "@/constants";

import DefaultList from "@/components/DefaultList";
import {
  ReadProjectSettlements,
  projectSettlementsRead,
} from "@/schemas/projectSettlements";
import { getProjectSettlementsListGetter } from "@/modules/projectSettlements/service/projectSettlements";
import { useCallback } from "react";
import { generatePath, useParams } from "react-router-dom";
import { useProjectBreadcrumb } from "@/modules/projects/hooks/useProjectBreadcrumb";
import { ProjectService } from "@/modules/projects/service/project";
import { useQuery } from "@tanstack/react-query";
import { useProjectSettlementsListColumnOverrides } from "@/modules/projects/hooks/useProjectSettlementsListColumnOverrides";
import Title from "@/components/Title";
import { ProjectDetailsAndNavigation } from "@/modules/projects/components/ProjectDetailsAndNavigation";
import { useTranslation } from "react-i18next";
import { FilterType } from "@/lib/filters";

const initialColumnVisibility: Record<string, boolean> = {
  period: true, // Okres
  project_ref_number: true,
  project_crs_number: true,
  project_name: false,
  client_name: false,
  client_tax_number: true,
  project_start_date: false,
  project_end_date: false,
  project_type: false,
  project_status: false,
  primary_manager: false,
  period_status: true, // Status okresu (obecnie Stan)
  planned_hours: true, // Godziny planowane od kienta
  planned_avg_rate: true, // Uśredniona planowana stawka od klienta
  planned_income: true, // Planowany przychód
  scheduled_hours: false,
  scheduled_avg_rate: false,
  scheduled_payment: false,
  planned_profitability: false,
  contractors_hours: false,
  contractors_avg_rate: false,
  contractors_payment: false,
  invoiced_hours: true, // Godziny zafakturowane
  invoiced_avg_rate: true, // Uśredniona zafakturowana stawka od klienta
  invoiced_income: true, // Kwota zafakturowana
  profitability: false,
};

const ProjectSettlementsList = () => {
  const { t } = useTranslation();
  const columnOverrides = useProjectSettlementsListColumnOverrides();

  const params = useParams();
  const projectId = params.projectId as string;

  const fetchList = useCallback(getProjectSettlementsListGetter(projectId), [
    projectId,
  ]);

  const projectQuery = useQuery({
    queryKey: [Module.Projects, projectId, "settlements-list"] as const,
    queryFn: () => ProjectService.get(projectId),
  });

  const breadcrumbs = useProjectBreadcrumb(projectQuery, [
    {
      label: t("projects.breadcrumb.projectSettlements"),
    },
  ]);

  return (
    <DefaultList<ReadProjectSettlements>
      schema={projectSettlementsRead}
      name="projectSettlements"
      fetchList={fetchList}
      pathBuilder={(row) =>
        generatePath(PATH_PROJECT_SETTLEMENTS_DETAILS, {
          projectId,
          settlementId: row.id,
        })
      }
      tableFeatures={{
        exportSettings: {
          moduleName: Module.ProjectSettlements,
          queryParams: {
            [`project_id[${FilterType.EqualsString}]`]: projectId,
          },
        },
        enableColumnVisibility: true,
        initialColumnVisibility,
      }}
      actions={[]}
      columnOverrides={columnOverrides}
      breadcrumbs={breadcrumbs}
      headerContent={
        <>
          <Title>{t("projectSettlements.title.single")}</Title>
          <ProjectDetailsAndNavigation />
        </>
      }
    />
  );
};

export default ProjectSettlementsList;
